import {
	Component,
	ElementRef,
	EventEmitter,
	HostListener,
	Input,
	OnChanges,
	Output,
	Renderer2,
	SimpleChanges
} from '@angular/core';
import { XMLParser } from 'fast-xml-parser';
import { translationsDe } from './i18n/de';
import { translationsEn } from './i18n/en';
import { HttpClient } from '@angular/common/http';

/**
 * Visualisiert eine E-Rechnung (XRechnung / ZUGFeRD-XML) als lesbares HTML.
 *
 * Ablauf in drei Stufen — die Komponente selbst rendert nichts, sie steuert nur die Kette:
 *
 *   1. `srcUrl` wird als Blob geladen und als Text nach `xmlContent` gelegt.
 *   2. Syntax-Erkennung: `fast-xml-parser` liefert die Wurzelelemente, deren Name entscheidet,
 *      welches Mapping-Stylesheet (CII / CIO / UBL / UBL-CreditNote) benutzt wird.
 *   3. Zwei XSLT-Transformationen via SaxonJS (global aus `assets/lib/saxon-js/`, s. angular.json):
 *        a) <Syntax>-xr.sef.json      → Zwischenformat "XR" (semantische EN-16931-Felder,
 *                                        Elementnamen wie `xr:Actual_delivery_date` + `xr:id="BT-72"`)
 *        b) xrechnung-html.smivl.sef  → fertiges HTML, das per Renderer2 in den Host gehängt wird
 *
 * Die `.sef.json`-Dateien sind die für SaxonJS vorkompilierten Fassungen der `.xsl`-Quellen im
 * selben Ordner — nach einer XSL-Änderung müssen sie neu erzeugt werden (s. `assets/xslt/README.md`).
 *
 * WICHTIG für die Fehlersuche "Feld fehlt in der Anzeige": ein Feld kann auf beiden Stufen
 * verloren gehen. Fehlt es schon im XR-Zwischenergebnis (Stufe 3a), ist die Ursache im
 * Mapping-Stylesheet, nicht in der HTML-Stufe.
 *
 * Eingebunden über `app-e-invoice-viewer` in: case.component.html, xml-viewer.component.html und
 * admin-mail-edit.component.html. Alle drei zeigen bei `onError` stattdessen `app-xml-view`
 * (Roh-XML-Baum) an — der Viewer ist also die "schöne" Variante mit Rohdaten-Fallback.
 */
@Component({
	selector: 'app-e-invoice-viewer',
	template: '',
	styleUrls: ['./e-invoice-viewer.component.scss']
})
export class EInvoiceViewerComponent implements OnChanges {
	/** URL, von der das XML geladen wird (Download-Endpunkt des FileDataService bzw. `pdfURL` im Case). */
	@Input() srcUrl!: string;
	/** Sprache der Feldbeschriftungen; wird als Stylesheet-Parameter `i18n` durchgereicht. */
	@Input() lang: 'de' | 'en' = 'de';
	/** true = zusätzlich die EN-16931-Kennung (z. B. "BT-72") neben der Beschriftung ausgeben. */
	@Input() showIds: boolean = false;
	/** Wird bei jedem Fehler der Kette gemeldet; die Aufrufer schalten dann auf `app-xml-view` um. */
	// eslint-disable-next-line @angular-eslint/no-output-on-prefix
	@Output() onError: EventEmitter<void> = new EventEmitter();

	xmlContent: string | undefined = undefined;
	/** Nur zur Erkennung der Syntax anhand der Wurzelelemente — nicht für die Anzeige. */
	private readonly xmlParser: XMLParser = new XMLParser();
	private readonly domParser: DOMParser = new DOMParser();
	private translations: any = translationsDe;

	constructor(
		private readonly renderer: Renderer2,
		private readonly elementRef: ElementRef,
		private readonly http: HttpClient
	) {}

	/**
	 * Stufe 1: XML laden, sobald `srcUrl` gesetzt wird; bei `lang`-Wechsel nur neu rendern.
	 *
	 * Achtung: `changes['srcUrl']` wird hier ungeprüft dereferenziert. Ändert ein Aufrufer allein
	 * `lang` oder `showIds`, ist der Eintrag undefined und die Methode läuft in einen TypeError.
	 * Heute unkritisch, weil alle drei Aufrufer `srcUrl` initial mitsetzen und `lang`/`showIds`
	 * danach nicht mehr ändern.
	 */
	ngOnChanges(changes: SimpleChanges) {
		const srcUrl = changes['srcUrl'];
		if (srcUrl.currentValue) {
			this.http.get(srcUrl.currentValue, { responseType: 'blob' }).subscribe({
				next: (fileBlob) => {
					fileBlob.text().then((content) => {
						this.xmlContent = content;
						this.displayXML();
					});
				},
				error: (err) => {
					console.error(err);
					this.onError.emit();
				}
			});
		}
		const langChanges = changes['lang'];
		if (langChanges) {
			switch (langChanges.currentValue) {
				case 'en':
					this.translations = translationsEn;
					break;
				case 'de':
				default:
					this.translations = translationsDe;
					break;
			}
			this.displayXML();
		}
	}

	/**
	 * Stufe 2: Syntax anhand des Wurzelelement-Namens erkennen und das passende Mapping-Stylesheet
	 * wählen. Verglichen wird gegen den *rohen* Schlüssel inkl. Namensraum-Präfix (`rsm:...`,
	 * `ubl:...`), daher `includes(...)` statt Gleichheit.
	 *
	 * Reihenfolge der Zweige ist bedeutsam: 'CrossIndustryInvoice' enthält 'Invoice', muss also
	 * vor dem UBL-Zweig geprüft werden. Aus demselben Grund kann der CreditNote-Zweig für ein UBL
	 * `<CreditNote>` nur greifen, weil dessen Name kein 'Invoice' enthält.
	 */
	private displayXML() {
		if (!this.xmlContent) return;
		const parsedXml = this.xmlParser.parse(this.xmlContent);
		try {
			let found = false;
			for (let key in parsedXml) {
				if (key.includes('CrossIndustryInvoice')) {
					// Display CII — UN/CEFACT CII (XRechnung-CII, ZUGFeRD/Factur-X)
					this.transformAndDisplay(this.xmlContent, 'cii-xr.sef.json');
					found = true;
				} else if (key.includes('SCRDMCCBDACIOMessageStructure')) {
					// Display CIO — UN/CEFACT Cross Industry Order
					this.transformAndDisplay(this.xmlContent, 'cio-xr.sef.json');
					found = true;
				} else if (key.includes('Invoice')) {
					// Display UBL — OASIS UBL 2.1 Invoice
					this.transformAndDisplay(this.xmlContent, 'ubl-xr.sef.json');
					found = true;
				} else if (key.includes('CreditNote')) {
					// Display UBLCN — OASIS UBL 2.1 CreditNote (Gutschrift)
					this.transformAndDisplay(this.xmlContent, 'ubl-creditnote-xr.sef.json');
					found = true;
				}
			}
			if (!found) {
				// TODO: Show message to user
				console.error(
					'File format not recognized',
					'Is it a UBL 2.1 or UN/CEFACT 2016b XML file or PDF you are trying to open?'
				);
				this.onError.emit();
			}
		} catch (err) {
			// TODO: Show message to user
			console.error(err);
			this.onError.emit();
		}
	}

	/**
	 * Stufe 3: die eigentliche XSLT-Kette.
	 *
	 * Vorab wird der Dokumenttyp (BT-3) per XPath aus dem Rohdokument gelesen, um Bestellungen
	 * (220/231) von Rechnungen zu unterscheiden — davon hängen nur die Feldbeschriftungen ab.
	 *
	 * ACHTUNG (Altlast, nicht anfassen ohne Auftrag): `rsm` ist hier auf den *ram*-Namensraum
	 * gemappt. Der echte rsm-Namensraum ist `...:CrossIndustryInvoice:100`. Der XPath
	 * `//rsm:ExchangedDocument/ram:TypeCode` kann damit nie treffen, `typecode` bleibt immer ''
	 * und `isOrder` immer false. Die gesamte Bestell-Umbenennung unten ist dadurch toter Code.
	 */
	transformAndDisplay(content: string, stylesheetFileName: string) {
		const namespaces: any = {
			rsm: 'urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100',
			ram: 'urn:un:unece:uncefact:data:standard:ReusableAggregateBusinessInformationEntity:100'
		};

		// Create a namespace resolver function
		function nsResolver(prefix: string | null): string | null {
			if (prefix === null) return null;
			return namespaces[prefix] || null;
		}

		const doc = this.domParser.parseFromString(content, 'application/xml');
		//cbc:InvoiceTypeCode for bis billing, ubl order would be <cbc:ProfileID>urn:fdc:peppol.eu:poacc:bis:ordering:3</cbc:ProfileID>
		const typecode = doc.evaluate(
			'//rsm:ExchangedDocument/ram:TypeCode',
			doc,
			nsResolver,
			XPathResult.STRING_TYPE,
			null
		).stringValue;

		const isOrder = typecode == '220' || typecode == '231';

		// Stufe 3a: konkrete Syntax (CII/CIO/UBL) → semantisches XR-Zwischenformat.
		// Hier entscheidet sich, welche EN-16931-Felder überhaupt weitergegeben werden; ein hier
		// nicht erzeugtes Feld kann die HTML-Stufe nicht mehr anzeigen.
		return SaxonJS.transform(
			{
				stylesheetLocation: `assets/xslt/${stylesheetFileName}`,
				sourceText: content,
				destination: 'serialized'
			},
			'async'
		)
			.then((output: any) => {
				let xrXML = output.principalResult;

				// Beschriftungen für Bestellungen umhängen. Das überschreibt die importierten
				// Übersetzungsobjekte dauerhaft (Modul-Singleton) — einmal umgestellt, bleibt es
				// für alle später angezeigten Dokumente umgestellt. Aktuell unwirksam, weil
				// `isOrder` durch das Namensraum-Problem oben nie true wird.
				if (isOrder) {
					this.translations['bt1'] = this.translations['bt1_order']; // Rechnungsnummer -> Bestellnummer
					this.translations['bt2'] = this.translations['bt2_order']; // Rechnungsdatum
					this.translations['bt3'] = this.translations['bt3_order']; // Rechnungart
					this.translations['bg22'] = this.translations['bg22_order']; // Gesamtbeträge der Rechnung
					this.translations['bt25'] = this.translations['bt25_order']; // Rechnungsnummer
					this.translations['bt26'] = this.translations['bt26_order']; // Rechnungsdatum
					this.translations['details'] = this.translations['details_order']; // Rechnungsdaten
				}

				// Stufe 3b: XR-Zwischenformat → HTML. `smivl` ist die SmartInvoice-Fassung der
				// KoSIT-Visualisierung (Tab-Layout: Übersicht / Positionen / Zusätze / Anlagen /
				// Laufzettel); daneben liegt unbenutzt die Originalfassung `xrechnung-html.uni`.
				// Die Beschriftungen kommen komplett als Stylesheet-Parameter `i18n` aus i18n/de|en.
				SaxonJS.transform(
					{
						stylesheetLocation: 'assets/xslt/xrechnung-html.smivl.sef.json',
						sourceText: xrXML,
						destination: 'serialized',
						stylesheetParams: {
							isOrder,
							showIds: this.showIds,
							'Q{}i18n': this.translations
						}
					},
					'async'
				)
					// Ergebnis-HTML in den (per `template: ''` leeren) Host hängen. Ein zuvor
					// eingehängter Baum wird vorher entfernt, damit ein Sprach- oder
					// Dokumentwechsel nicht zwei Ansichten übereinander stapelt.
					.then((response: any) => {
						if (this.elementRef.nativeElement.firstChild) {
							this.renderer.removeChild(
								this.elementRef.nativeElement,
								this.elementRef.nativeElement.firstChild
							);
						}
						this.renderer.appendChild(
							this.elementRef.nativeElement,
							this.domParser.parseFromString(response.principalResult, 'text/html').body
						);
					})
					.catch((error: any) => {
						console.error(error);
						this.onError.emit();
					});
			})
			.catch((error: any) => {
				console.error(error);
				this.onError.emit();
			});
	}

	/**
	 * Tab-Umschaltung für das eingehängte HTML. Das Stylesheet liefert nur statisches Markup ohne
	 * Skript, deshalb übernimmt die Komponente die Interaktion: sie hört global auf Klicks,
	 * reagiert auf `button.tab` und tauscht Sichtbarkeit (`divShow`) sowie Aktiv-Zustand
	 * (`btnAktiv`) um. Die IDs entsprechen den Panels aus xrechnung-html.smivl.xsl.
	 */
	@HostListener('document:click', ['$event'])
	handleMenuItemClick(event: MouseEvent) {
		const target = event.target as HTMLElement;
		if (target.matches('button.tab')) {
			document.getElementsByClassName('btnAktiv')[0]?.classList.remove('btnAktiv');
			target.classList.add('btnAktiv');

			const visibleTabContent = document.getElementsByClassName('divShow')[0];
			if (visibleTabContent) {
				this.renderer.setStyle(visibleTabContent, 'display', 'none');
				this.renderer.removeClass(visibleTabContent, 'divShow');
			}
			switch (target.id) {
				case 'menueUebersicht':
					const uebersicht = document.getElementById('uebersicht');
					this.renderer.setStyle(uebersicht, 'display', 'block');
					this.renderer.addClass(uebersicht, 'divShow');
					break;
				case 'menueDetails':
					const details = document.getElementById('details');
					this.renderer.setStyle(details, 'display', 'block');
					this.renderer.addClass(details, 'divShow');
					break;
				case 'menueZusaetze':
					const zusaetze = document.getElementById('zusaetze');
					this.renderer.setStyle(zusaetze, 'display', 'block');
					this.renderer.addClass(zusaetze, 'divShow');
					break;
				case 'menueAnlagen':
					const anlagen = document.getElementById('anlagen');
					this.renderer.setStyle(anlagen, 'display', 'block');
					this.renderer.addClass(anlagen, 'divShow');
					break;
				case 'menueLaufzettel':
					const laufzettel = document.getElementById('laufzettel');
					this.renderer.setStyle(laufzettel, 'display', 'block');
					this.renderer.addClass(laufzettel, 'divShow');
					break;
			}
		}
	}
}
